import { html, customElement } from '@adobe/edex/ui/edex-lit';
import { EdexTagName, EdexDomID, AdobeAnalyticsEvent } from '@adobe/edex/ui/shared/constants';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-home';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-help';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-classic-grid-view';
import { RouterPageElement } from '@adobe/edex/ui/components/base.element';
import { fetchAllPlaylists, fetchPlaylist } from '../../store/routines';
import { selectPlaylist } from '../../store/selectors';
import { router } from '../../config/router';
import './playlistView.scss';
import { EntityType, PlaylistSchema } from '@adobe/edex/ui/shared/types';
import '@adobe/edex/ui/components/shared/socialMediaShare/socialMediaShare';
import { AnalyticsEvent } from '../../constants';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import '@adobe/edex/ui/components/wrappers/resource-card-wrapper';
import '@adobe/edex/ui/components/my-edex/emptyState';
import {
  formatDocumentTitleAsSuffix,
  getAvatarURL,
  getDomainOfURL,
  getPlaylistImageURL,
} from '@adobe/edex/ui/shared/utils';
import { EdexSEO } from '@adobe/edex/ui/shared/types/ui';
import { processSEO } from '@adobe/edex/ui/shared/utils/index';
import { isEmpty } from 'lodash-es';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { $t } from '@adobe/edex/ui/shared/config/i18n';

import(
  /* webpackChunkName: "component/not-found-element-styles" */
  /* webpackMode: "lazy" */
  '@adobe/edex/ui/components/not-found/not-found.scss'
);

@customElement(EdexTagName.playlistView)
export class PlaylistView extends RouterPageElement {
  get useLightDom() {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      loading: { attribute: false },
      notFound: { attribute: false },
      playlist: { type: Object },
    };
  }
  loading = true;
  notFound = false;
  playlist: PlaylistSchema;
  siteSection = 'Playlists';

  get seo(): EdexSEO {
    if (!this.playlist || isEmpty(this.playlist.id)) {
      return null;
    }
    const {
      seoProps: { title, description, image },
      playlist,
    } = this;
    return {
      title,
      canonicalURL: document.location.href,
      meta: [
        {
          property: 'og:title',
          content: `${playlist.title} by ${playlist.author.fullName}`,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: document.location.href,
        },
        {
          property: 'og:image',
          content: image,
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '627',
        },

        //Twitter Metatag
        {
          property: 'twitter:url',
          content: document.location.href,
        },
        {
          property: 'twitter:domain',
          content: getDomainOfURL(document.location.origin),
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:title',
          content: `${playlist.title} by ${playlist.author.fullName}`,
        },
        {
          name: 'twitter:description',
          content: description,
        },
        {
          name: 'twitter:image',
          content: image,
        },
      ],
    };
  }

  get seoProps() {
    return {
      title: formatDocumentTitleAsSuffix('Playlists'),
      description: 'Bring creativity into your classroom with resources selected by other educators',
      image: getPlaylistImageURL(),
    };
  }

  actions = {
    fetchPlaylist: fetchPlaylist.trigger,
    fetchAllPlaylists: fetchAllPlaylists.trigger,
  };

  stateChanged(state) {
    super.stateChanged(state);
    const { playlist, loading } = selectPlaylist(state);
    this.loading = loading.playlist;
    this.notFound = loading.notFound;
    this.playlist = playlist;
  }

  async updated(changedProperties) {
    const playlistChanged = !!(
      !!this.playlist &&
      changedProperties.has('playlist') &&
      changedProperties.get('playlist')?.id !== this.playlist.id
    );
    if (playlistChanged && !isEmpty(this.playlist.id)) {
      processSEO(this.seo);
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    const { vanityURL, memberVanityURL } = router.location.params;
    this.actions.fetchPlaylist({
      id: vanityURL.toString(),
      memberVanityURL: memberVanityURL.toString(),
      limit: 200,
      offset: 0,
    });
  }

  handleShare(event) {
    event.preventDefault();
    this.track({
      event: AnalyticsEvent.playlistClickShare,
      payload: { id: this.playlist.id, socialNetworkID: event.detail.name.toLowerCase() },
    });

    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.playlistShare, 'Playlist - Share');
  }

  render() {
    const { playlist, loading, notFound } = this;
    return loading
      ? html`
          <edex-loader />
        `
      : notFound
      ? html`
          <edex-not-found .playlistNotFound=${notFound}></edex-not-found>
        `
      : html`
          <div id="${EdexDomID.playlistView}" class="playlist-view-page">
            <div id="author-container" class="grid-column-full bg-white">
              <h1 class="author-name">${playlist.title}</h1>
              <sp-avatar
                size="1500"
                label=${playlist.author.fullName}
                src="${getAvatarURL(playlist.author.avatarURL)}"
              ></sp-avatar>
              <p class="curated-by">Curated by</p>
              <h2>${playlist.author.fullName}</h2>
              ${playlist.author.awards?.length > 0
                ? html`
                    <ul>
                      ${playlist.author.awards.map(
                        (award) =>
                          html`
                            <li title=${$t(award.i18nLabel)}><img src=${award.imageURL} /></li>
                          `
                      )}
                    </ul>
                  `
                : null}
              <p class="playlist-description">${playlist.description}</p>
              <social-media-share .link=${window.location.href} @share=${(e) => this.handleShare(e)} />
            </div>
            ${playlist?.items.data.length > 0
              ? html`
                  <div class="playlist-grid-resources">
                    ${playlist.items.data.map((item) => {
                      if (item.type === EntityType.Resource) {
                        return html`
                          <edex-resource-card-wrapper .resource=${item.entityData}></edex-resource-card-wrapper>
                        `;
                      }
                    })}
                  </div>
                `
              : html`
                  <my-edex-empty-state
                    description=${$t(I18nKey.consumerViewResourceEmptyDescription)}
                    iconSrc="ui/assets/MT_Folder.svg"
                    .showButton=${false}
                  />
                `}
          </div>
        `;
  }
}
